import { memo, useCallback } from "react";
import { generatePath, useNavigate } from "react-router";

import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { Button } from "@/components/ui/button/button";

import { useSkipHotelTemplateSettings } from "../../api";

type Props = {
	hotelID: string;
};

export const SelectHotelTemplateHeader = memo(({ hotelID }: Props) => {
	const { skipHotelTemplateSettings } = useSkipHotelTemplateSettings();
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const navigate = useNavigate();

	const handleClick = useCallback(async () => {
		showLoadingScreenCallback();

		const { data, errors } = await skipHotelTemplateSettings(hotelID).finally(() => {
			hideLoadingScreenCallback();
		});

		if (!data || errors) {
			notifyUserEventInfo({
				title: "テンプレートのスキップに失敗しました",
				description: "時間を置いて再度お試しください",
			});

			return;
		}

		const path = generatePath(PATHS.HOTEL.TOP, { hotelID });
		navigate(path, { replace: true });
	}, [hotelID, navigate, skipHotelTemplateSettings, showLoadingScreenCallback, hideLoadingScreenCallback]);

	return (
		<div className="flex flex-col items-baseline space-y-4 md:flex-row md:justify-between">
			<div>
				<h3 className="text-3xl font-bold">テンプレートを選んで簡単に予約ページをつくる</h3>
				<p className="mt-4 text-zinc-950">
					ご希望の設定に近いテンプレートを利用することで簡単に予約ページを作成できます。
					<br />
					画像や文章、価格などは、テンプレート選択後に、変更可能です。
				</p>
			</div>

			<div className="flex w-full items-start justify-end md:w-auto md:justify-start">
				<Button variant="outline" onClick={handleClick}>
					スキップする
				</Button>
			</div>
		</div>
	);
});

SelectHotelTemplateHeader.displayName = "SelectHotelTemplateHeader";
